// 057A — Escalas Geriátricas (Fase 7, Tanda 7, ver PLAN.md). Transcripción del PDF oficial del Anexo 1 MSP: hoja apaisada
// idx 207 (footer `SNS-MSP/HCU-form.057A/2021`). Informe de brechas en `docs/hce-fase7/brechas-121-057-057A-060.md`.
//
// El papel trae 5 escalas: Lawton y Brody, Timed Get Up and Go, Pfeiffer, Yesavage (5 ítems) y Barthel. El
// Questionnaire anterior inventaba un "Mini-Mental" que no está impreso; ya no se usa (el registro discreto lo reemplaza).
//
// Decisiones ante lo que el papel no resuelve (la más conservadora):
//  - Lawton: el puntaje sale de la columna "Puntos" impresa; los ítems con `*` (3, 4 y 5) no puntúan en hombres
//    (impreso: máx. 5 hombres, 8 mujeres). Sin bandas de interpretación (no hay).
//  - TUG: las bandas impresas se solapan (<10, <20, 20-29, >20): se muestran como leyenda y NO se interpreta el valor.
//  - Pfeiffer: el corte impreso es 3 errores (sabe leer y escribir) o 4 (no); se aplica según lo que se marque.
//  - Yesavage: corte impreso ≥ 2. Barthel: sin bandas impresas en esta hoja (las de 058A son de otro formulario).
// Sin FHIR a propósito: las escalas no tienen consumidor demostrable; quedan como `Par[]` en el PDF firmado.
import { paresDeCampos, valoresVacios, type CampoSimple, type OpcionCampo, type Valores } from './camposSimplesDatos';
import {
  calcularTotalEscala,
  paresDeEscalaClinica,
  type DefinicionEscalaClinica,
  type OpcionEscalaClinica,
  type RespuestasEscalaClinica,
} from './uci/EscalaClinica';
import type { Par } from './index';

// ---------- 1. Lawton y Brody ----------

export interface OpcionLawton {
  readonly etiqueta: string;
  readonly puntos: 0 | 1;
}

export interface ItemLawton {
  readonly clave: string;
  readonly etiqueta: string;
  /** Marcado con `*` en el papel: no puntúa en hombres. */
  readonly noPuntuaEnHombres: boolean;
  readonly opciones: readonly OpcionLawton[];
}

const o = (etiqueta: string, puntos: 0 | 1): OpcionLawton => ({ etiqueta, puntos });

export const ITEMS_LAWTON: readonly ItemLawton[] = [
  {
    clave: 'telefono',
    etiqueta: 'Capacidad para usar el teléfono',
    noPuntuaEnHombres: false,
    opciones: [o('Utiliza el teléfono por iniciativa propia', 1), o('Es capaz de marcar bien algunos números familiares', 1), o('Es capaz de contestar el teléfono, pero no de marcar', 1), o('No utiliza el teléfono', 0)],
  },
  {
    clave: 'compras',
    etiqueta: 'Hacer compras',
    noPuntuaEnHombres: false,
    opciones: [o('Realiza todas las compras necesarias independientemente', 1), o('Realiza independientemente pequeñas compras', 0), o('Necesita ir acompañado para realizar cualquier compra', 0), o('Totalmente incapaz de comprar', 0)],
  },
  {
    clave: 'comida',
    etiqueta: 'Preparación de la comida *',
    noPuntuaEnHombres: true,
    opciones: [
      o('Organiza, prepara y sirve las comidas por sí solo adecuadamente', 1),
      o('Prepara adecuadamente las comidas si se le proporcionan los ingredientes', 0),
      o('Prepara, calienta y sirve las comidas, pero no sigue una dieta adecuada', 0),
      o('Necesita que le preparen y sirvan las comidas', 0),
    ],
  },
  {
    clave: 'casa',
    etiqueta: 'Cuidado de la casa *',
    noPuntuaEnHombres: true,
    opciones: [
      o('Mantiene la casa solo o con ayuda ocasional (para trabajos pesados)', 1),
      o('Realiza tareas ligeras, como lavar los platos o hacer las camas', 1),
      o('Realiza tareas ligeras, pero no puede mantener un adecuado nivel de limpieza', 1),
      o('Necesita ayuda en todas las labores de la casa', 1),
      o('No participa en ninguna labor de la casa', 0),
    ],
  },
  {
    clave: 'ropa',
    etiqueta: 'Lavado de la ropa *',
    noPuntuaEnHombres: true,
    opciones: [o('Lava por sí solo toda su ropa', 1), o('Lava por sí solo pequeñas prendas', 1), o('Todo el lavado de ropa debe ser realizado por otro', 0)],
  },
  {
    clave: 'transporte',
    etiqueta: 'Uso de medios de transporte',
    noPuntuaEnHombres: false,
    opciones: [
      o('Viaja solo en transporte público o conduce su propio coche', 1),
      o('Es capaz de coger un taxi, pero no usa otro medio de transporte', 1),
      o('Viaja en transporte público cuando va acompañado por otra persona', 1),
      o('Utiliza el taxi o el automóvil solo con ayuda de otros', 0),
      o('No viaja en absoluto', 0),
    ],
  },
  {
    clave: 'medicacion',
    etiqueta: 'Responsabilidad respecto a su medicación',
    noPuntuaEnHombres: false,
    opciones: [o('Es capaz de tomar su medicación a la hora y dosis correcta', 1), o('Toma su medicación si la dosis es preparada previamente', 0), o('No es capaz de administrarse su medicación', 0)],
  },
  {
    clave: 'economia',
    etiqueta: 'Manejo de sus asuntos económicos',
    noPuntuaEnHombres: false,
    opciones: [o('Se encarga de sus asuntos económicos por sí solo', 1), o('Realiza las compras de cada día, pero necesita ayuda en las grandes compras, banco', 1), o('Incapaz de manejar dinero', 0)],
  },
];

/** `Patient.gender` FHIR -> sexo de Lawton; `other`/`unknown`/ausente = `''` (no se asume: se pide en pantalla). */
export function sexoLawtonDePaciente(gender: string | undefined): 'hombre' | 'mujer' | '' {
  return gender === 'male' ? 'hombre' : gender === 'female' ? 'mujer' : '';
}

export const OPCIONES_SEXO_LAWTON: readonly OpcionCampo[] = [
  { valor: 'hombre', etiqueta: 'Hombre' },
  { valor: 'mujer', etiqueta: 'Mujer' },
];

/** Respuestas de Lawton = índice de la opción elegida por ítem, como texto (varias opciones comparten puntaje). */
export type RespuestasLawton = Readonly<Record<string, string>>;

export function puntosLawton(item: ItemLawton, respuesta: string | undefined): number | undefined {
  if (respuesta === undefined || respuesta === '') return undefined;
  return item.opciones[Number(respuesta)]?.puntos;
}

/** `undefined` mientras falte el sexo o algún ítem que puntúa. Máximo impreso: 5 hombres (sin ítems *), 8 mujeres. */
export function totalLawton(respuestas: RespuestasLawton, sexo: string): { readonly total: number; readonly maximo: number } | undefined {
  if (sexo !== 'hombre' && sexo !== 'mujer') return undefined;
  let total = 0;
  let maximo = 0;
  for (const item of ITEMS_LAWTON) {
    if (sexo === 'hombre' && item.noPuntuaEnHombres) continue;
    const p = puntosLawton(item, respuestas[item.clave]);
    if (p === undefined) return undefined;
    total += p;
    maximo += 1;
  }
  return { total, maximo };
}

// ---------- 2. Timed Get Up and Go ----------

export const INSTRUCCIONES_TUG =
  'Sentado con la espalda apoyada, el paciente se levanta, camina 3 metros, da media vuelta, vuelve y se sienta. Se cronometra desde que empieza a levantarse hasta que se sienta.';

/** Bandas tal como están impresas: se solapan (<10 dentro de <20; 20-29 dentro de >20), por eso no se interpreta el valor. */
export const BANDAS_TUG = ['< 10: Movilidad independiente', '< 20: Mayormente independiente', '20-29: Movilidad variable', '> 20: Movilidad reducida'] as const;

export const CAMPOS_TUG: readonly CampoSimple[] = [{ clave: 'segundos', etiqueta: 'Puntaje (segundos)', tipo: 'numero' }];

// ---------- 3. Pfeiffer ----------

const ERROR: readonly OpcionEscalaClinica[] = [
  { valor: 0, etiqueta: '0 — sin error' },
  { valor: 1, etiqueta: '1 — error' },
];

const ITEMS_PFEIFFER_ANTES = [
  { clave: 'p1', etiqueta: '¿Qué día es hoy? (día, mes, año)', opciones: ERROR },
  { clave: 'p2', etiqueta: '¿Qué día de la semana es hoy?', opciones: ERROR },
  { clave: 'p3', etiqueta: '¿Dónde estamos ahora?', opciones: ERROR },
] as const;
const ITEM_PFEIFFER_TELEFONO = { clave: 'p4', etiqueta: '¿Cuál es su número de teléfono?', opciones: ERROR } as const;
const ITEM_PFEIFFER_DIRECCION = { clave: 'p4a', etiqueta: '¿Cuál es su dirección? (solo si no tiene teléfono)', opciones: ERROR } as const;
const ITEMS_PFEIFFER_DESPUES = [
  { clave: 'p5', etiqueta: '¿Cuántos años tiene?', opciones: ERROR },
  { clave: 'p6', etiqueta: '¿Cuál es su fecha de nacimiento? (día, mes, año)', opciones: ERROR },
  { clave: 'p7', etiqueta: '¿Quién es ahora el presidente del gobierno?', opciones: ERROR },
  { clave: 'p8', etiqueta: '¿Quién fue el anterior presidente del gobierno?', opciones: ERROR },
  { clave: 'p9', etiqueta: '¿Cuáles son los dos apellidos de su madre?', opciones: ERROR },
  { clave: 'p10', etiqueta: 'Vaya restando de 3 en 3 al número 20 hasta llegar a 0', opciones: ERROR },
] as const;

/** 10 respuestas: la dirección reemplaza al teléfono solo si el paciente no tiene teléfono. */
export function definicionPfeiffer057A(sinTelefono: boolean): DefinicionEscalaClinica {
  return { items: [...ITEMS_PFEIFFER_ANTES, sinTelefono ? ITEM_PFEIFFER_DIRECCION : ITEM_PFEIFFER_TELEFONO, ...ITEMS_PFEIFFER_DESPUES], rangos: [] };
}

export const NOTA_PFEIFFER =
  'El punto de corte está en 3 o más errores, en el caso de personas que al menos sepan leer y escribir y de 4 o más para los que no. A partir de esa puntuación existe la sospecha de deterioro cognitivo.';

export const OPCIONES_LEER_ESCRIBIR: readonly OpcionCampo[] = [
  { valor: 'si', etiqueta: 'Sabe leer y escribir' },
  { valor: 'no', etiqueta: 'No sabe leer y escribir' },
];

/** Corte impreso según lectura/escritura; `undefined` si no se indicó. */
export function cortePfeiffer(sabeLeerEscribir: string): number | undefined {
  return sabeLeerEscribir === 'si' ? 3 : sabeLeerEscribir === 'no' ? 4 : undefined;
}

// ---------- 4. Yesavage (5 ítems, numeración del GDS-15) ----------

const si = (puntos: 0 | 1): readonly OpcionEscalaClinica[] => [
  { valor: puntos, etiqueta: `Sí (${puntos})` },
  { valor: puntos === 1 ? 0 : 1, etiqueta: `No (${puntos === 1 ? 0 : 1})` },
];

export const DEFINICION_YESAVAGE: DefinicionEscalaClinica = {
  items: [
    { clave: 'y3', etiqueta: '3- ¿Siente que su vida está vacía?', opciones: si(1) },
    { clave: 'y4', etiqueta: '4- ¿Se siente con frecuencia aburrido/a?', opciones: si(1) },
    { clave: 'y5', etiqueta: '5- ¿Se encuentra de buen humor la mayor parte del tiempo?', opciones: si(0) },
    { clave: 'y8', etiqueta: '8- ¿Con frecuencia se siente desamparado/a, desprotegido?', opciones: si(1) },
    { clave: 'y13', etiqueta: '13- ¿Se siente lleno/a de energía?', opciones: si(0) },
  ],
  rangos: [],
};

export const CORTE_YESAVAGE = 2;

// ---------- 5. Barthel ----------

const op = (valor: number, etiqueta: string): OpcionEscalaClinica => ({ valor, etiqueta: `${etiqueta} (${valor})` });

// Definiciones de cada opción tal como están impresas en la hoja (título en negrita + descripción).
export const DEFINICION_BARTHEL_057A: DefinicionEscalaClinica = {
  items: [
    {
      clave: 'comer',
      etiqueta: 'Comer',
      opciones: [
        op(10, 'Independiente: Capaz de comer por sí solo en un tiempo razonable. La comida puede ser cocinada y servida por otra persona'),
        op(5, 'Necesita ayuda: Para cortar la carne, extender la mantequilla, etc, pero es capaz de comer solo'),
        op(0, 'Dependiente: Necesita ser alimentado por otra persona'),
      ],
    },
    {
      clave: 'asearse',
      etiqueta: 'Asearse',
      opciones: [op(5, 'Independiente: Capaz de lavarse entero, de entrar y salir del baño sin ayuda y de hacerlo sin necesidad de que otra persona supervise'), op(0, 'Dependiente: Necesita algún tipo de ayuda o supervisión')],
    },
    {
      clave: 'vestirse',
      etiqueta: 'Vestirse',
      opciones: [op(10, 'Independiente: Capaz de ponerse y quitarse la ropa sin ayuda'), op(5, 'Necesita ayuda: Realiza sin ayuda más de la mitad de estas tareas en un tiempo razonable'), op(0, 'Dependiente: Necesita ayuda para las mismas')],
    },
    {
      clave: 'arreglarse',
      etiqueta: 'Arreglarse',
      opciones: [op(5, 'Independiente: Realiza todas las actividades personales sin ayuda alguna. Los complementos pueden ser provistos por otra persona'), op(0, 'Dependiente: Necesita alguna ayuda')],
    },
    {
      clave: 'deposicion',
      etiqueta: 'Deposición',
      opciones: [
        op(10, 'Continente: No presenta episodios de incontinencia'),
        op(5, 'Accidente ocasional: Menos de una vez por semana o necesita ayuda para colocar enemas o supositorios'),
        op(0, 'Incontinente: Más de un episodio semanal. Incluye administración de enemas o supositorios por otra persona'),
      ],
    },
    {
      clave: 'miccion',
      etiqueta: 'Micción',
      opciones: [
        op(10, 'Continente: No presenta episodios de incontinencia. Capaz de utilizar cualquier dispositivo por sí solo (sonda, orinal, pañal, etc)'),
        op(5, 'Accidente ocasional: Presenta un máximo de un episodio en 24 horas o requiere ayuda para la manipulación de sondas u otros dispositivos'),
        op(0, 'Incontinente: Más de un episodio en 24 horas. Incluye pacientes con sonda incapaces de manejarse'),
      ],
    },
    {
      clave: 'retrete',
      etiqueta: 'Ir al retrete',
      opciones: [
        op(10, 'Independiente: Entra y sale solo y no necesita ayuda alguna por parte de otra persona'),
        op(5, 'Necesita ayuda: Capaz de manejarse con una pequeña ayuda; es capaz de usar el baño. Puede limpiarse solo'),
        op(0, 'Dependiente: Incapaz de acceder a él o de utilizarlo sin ayuda mayor'),
      ],
    },
    {
      clave: 'traslado',
      etiqueta: 'Traslado cama/sillón',
      opciones: [
        op(15, 'Independiente: No requiere ayuda para sentarse o levantarse de una silla ni para entrar o salir de la cama'),
        op(10, 'Mínima ayuda: Incluye una supervisión o una pequeña ayuda física'),
        op(5, 'Gran ayuda: Precisa la ayuda de una persona fuerte o entrenada. Capaz de estar sentado sin ayuda'),
        op(0, 'Dependiente: Necesita una grúa o el alzamiento por dos personas. Es incapaz de permanecer sentado'),
      ],
    },
    {
      clave: 'deambulacion',
      etiqueta: 'Deambulación',
      opciones: [
        op(15, 'Independiente: Puede caminar 50 metros o su equivalente en casa sin ayuda ni supervisión. Puede utilizar cualquier ayuda mecánica excepto su andador. Si utiliza una prótesis, puede ponérsela y quitársela solo'),
        op(10, 'Necesita ayuda: Necesita supervisión o una pequeña ayuda física para andar 50 metros por parte de otra persona o utiliza andador'),
        op(5, 'Independiente en silla de ruedas: En 50 metros debe ser capaz de desplazarse, atravesar puertas y doblar esquinas solo'),
        op(0, 'Dependiente en silla de ruedas: precisa ser empujado por otro'),
      ],
    },
    {
      clave: 'escaleras',
      etiqueta: 'Subir y bajar escaleras',
      opciones: [op(10, 'Independiente: Capaz de subir y bajar un piso sin ayuda ni supervisión de otra persona'), op(5, 'Necesita ayuda: Necesita ayuda o supervisión'), op(0, 'Dependiente: Es incapaz de salvar escalones. Necesita ascensor')],
    },
  ],
  rangos: [],
};

// ---------- Estado ----------

export interface EstadoEscalasGeriatricas {
  readonly sexoLawton: string;
  readonly lawton: RespuestasLawton;
  readonly tug: Valores;
  readonly sabeLeerEscribir: string;
  readonly pfeifferSinTelefono: boolean;
  readonly pfeiffer: RespuestasEscalaClinica;
  readonly yesavage: RespuestasEscalaClinica;
  readonly barthel: RespuestasEscalaClinica;
}

export function estadoInicialEscalasGeriatricas(): EstadoEscalasGeriatricas {
  return { sexoLawton: '', lawton: {}, tug: valoresVacios(CAMPOS_TUG), sabeLeerEscribir: '', pfeifferSinTelefono: false, pfeiffer: {}, yesavage: {}, barthel: {} };
}

/** Serializa las 5 escalas a `Par[]`; los totales solo aparecen con la escala completa. */
export function paresDeEscalasGeriatricas(e: EstadoEscalasGeriatricas): Par[] {
  const pares: Par[] = [];

  const opcionLawton = (item: ItemLawton): string => {
    const r = e.lawton[item.clave];
    const opcion = r === undefined || r === '' ? undefined : item.opciones[Number(r)];
    return opcion ? `${opcion.etiqueta} (${opcion.puntos})` : '';
  };
  pares.push({ pregunta: 'Lawton y Brody — sexo (para el puntaje)', respuesta: OPCIONES_SEXO_LAWTON.find((x) => x.valor === e.sexoLawton)?.etiqueta ?? '' });
  for (const item of ITEMS_LAWTON) pares.push({ pregunta: `Lawton y Brody — ${item.etiqueta}`, respuesta: opcionLawton(item) });
  const lawton = totalLawton(e.lawton, e.sexoLawton);
  if (lawton) pares.push({ pregunta: 'Lawton y Brody — Total', respuesta: `${lawton.total} de ${lawton.maximo}` });

  pares.push(...paresDeCampos(CAMPOS_TUG, e.tug, 'Timed Get Up and Go — '));

  const defPfeiffer = definicionPfeiffer057A(e.pfeifferSinTelefono);
  pares.push(...paresDeEscalaClinica(defPfeiffer, e.pfeiffer, 'Pfeiffer — Total de errores'));
  const totalPfeiffer = calcularTotalEscala(defPfeiffer, e.pfeiffer);
  const corte = cortePfeiffer(e.sabeLeerEscribir);
  pares.push({ pregunta: 'Pfeiffer — lectura y escritura', respuesta: OPCIONES_LEER_ESCRIBIR.find((x) => x.valor === e.sabeLeerEscribir)?.etiqueta ?? '' });
  if (totalPfeiffer !== undefined && corte !== undefined) pares.push({ pregunta: 'Pfeiffer — corte', respuesta: `${corte} errores — ${totalPfeiffer >= corte ? 'sospecha de deterioro cognitivo' : 'por debajo del corte'}` });

  const totalYesavage = calcularTotalEscala(DEFINICION_YESAVAGE, e.yesavage);
  pares.push(...paresDeEscalaClinica(DEFINICION_YESAVAGE, e.yesavage, 'Yesavage — Total'));
  if (totalYesavage !== undefined) pares.push({ pregunta: 'Yesavage — corte', respuesta: totalYesavage >= CORTE_YESAVAGE ? 'considerar depresión (puntaje ≥ 2)' : 'por debajo del corte (< 2)' });

  pares.push(...paresDeEscalaClinica(DEFINICION_BARTHEL_057A, e.barthel, 'Barthel — Total'));

  return pares.filter((p) => p.respuesta.trim() !== '');
}
